import React, { useState } from 'react';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import logger from '@/logger';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import {
	RejectVanCreditRequestInput,
	useRejectVanCreditRequestMutation,
	RejectVanCreditRequestMutation
} from '@/graphql/__generated__/van-credits';

const RejectModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	vanCreditRequestId: string;
	onSuccess?: () => void;
}> = ({ isOpen, onClose, vanCreditRequestId, onSuccess }) => {
	const t = useTranslations('VanCreditRequestDetails');
	const [formData, setFormData] = useState<RejectVanCreditRequestInput>({
		requestId: '',
		rejectionReason: ''
	});
	const [formErrors, setFormErrors] = useState<Record<keyof RejectVanCreditRequestInput, string>>({
		requestId: '',
		rejectionReason: ''
	});

	const creditRequestsClient = useApolloClient(SCHEMAS.VAN_CREDITS);

	const [rejectVanCreditRequest, { loading }] = useRejectVanCreditRequestMutation({
		client: creditRequestsClient,
		onCompleted: async (data: RejectVanCreditRequestMutation) => {
			const rejectVanCreditResult = data.rejectVanCreditRequest;
			if (rejectVanCreditResult?.successful) {
				logger.success('Van credit request rejected successfully', rejectVanCreditResult);
				toast.success(t('VanCreditRejectedSuccessfully'));
				onClose();
				if (onSuccess) {
					onSuccess();
				}
			}
			if (
				rejectVanCreditResult?.mutationErrors &&
				rejectVanCreditResult.mutationErrors.length > 0
			) {
				let hasFieldErrors = false;

				rejectVanCreditResult.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'rejection_reason') {
									setFormErrors((prev) => ({
										...prev,
										rejectionReason: error.message || t('RejectionReasonRequired')
									}));
									hasFieldErrors = true;
								}
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorRejectingVanCredit'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error rejecting van credit', error);
			toast.error(t('ErrorRejectingVanCredit'));
		}
	});

	// Set initial microcredit request ID
	React.useEffect(() => {
		if (vanCreditRequestId) {
			setFormData((prev) => ({
				...prev,
				requestId: vanCreditRequestId
			}));
		}
	}, [vanCreditRequestId]);

	const handleRejectReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setFormData((prev) => ({
			...prev,
			rejectionReason: value
		}));

		if (formErrors.rejectionReason) {
			setFormErrors((prev) => ({
				...prev,
				rejectionReason: ''
			}));
		}
	};

	const handleRejectMicrocredit = async () => {
		if (!formData.rejectionReason) {
			setFormErrors((prev) => ({
				...prev,
				rejectionReason: t('RejectionReasonRequired')
			}));
			return;
		}
		setFormErrors({
			requestId: '',
			rejectionReason: ''
		});

		await rejectVanCreditRequest({
			variables: {
				input: formData
			}
		});
	};

	const rejectionReasonHint =
		t('RejectionReasonLength') + '. ' + formData.rejectionReason?.length + '/3400';

	return (
		<ConfirmModal
			isOpen={isOpen}
			confirmActionText={t('Reject')}
			title={t('RejectVanCredit')}
			onClose={onClose}
			onCancel={onClose}
			onConfirm={handleRejectMicrocredit}
			maxWidth={600}
			subtitle={t('RejectVanCreditSubtitle')}
			confirmBtnLoading={loading}
		>
			<div className="flex flex-col gap-1 py-4">
				<Typography size="md">{t('RejectReasonLabel')}</Typography>
				<div className="flex flex-col gap-1">
					<TextArea
						rows={4}
						value={formData.rejectionReason || ''}
						onChange={handleRejectReasonChange}
						placeholder={t('EnterRejectReason')}
						error={!!formErrors.rejectionReason}
						hint={rejectionReasonHint}
						maxChars={3400}
						strictMaxChars={true}
					/>
					{formErrors.rejectionReason && (
						<Typography size="sm" color="error">
							{formErrors.rejectionReason}
						</Typography>
					)}
				</div>
			</div>
		</ConfirmModal>
	);
};

export default RejectModal;
